import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Upload } from 'antd';
import { UploadChangeParam, UploadProps } from 'antd/es/upload';
import { FormItemProps } from 'antd/es/form';
import { FieldConfig, FieldMetaProps, useField } from 'formik';
import { UploadFile } from 'antd/es/upload/interface';
import { twoLevelShallowEqualObjects, getBase64 } from '@core/utils';

export type UploadFieldProps = FieldConfig & {
  item?: FormItemProps;
  input?: UploadProps;
  children: (params: { previewUrl: string | null }) => JSX.Element;
};

export type MemoizedUploadFieldProps = {
  item?: FormItemProps;
  input?: UploadProps;
  field: any;
  meta: FieldMetaProps<any>;
  children: (params: { previewUrl: string | null }) => JSX.Element;
  previewUrl: string | null;
};

const MemoizedUploadField = memo<MemoizedUploadFieldProps>(({ item, input, field, meta, previewUrl, children }) => {
  return (
    <Form.Item {...item} validateStatus={meta.touched && !!meta.error ? 'error' : undefined} help={meta.error}>
      <Upload beforeUpload={() => false} {...field} {...input}>
        {children({ previewUrl })}
      </Upload>
    </Form.Item>
  );
}, twoLevelShallowEqualObjects);

export const UploadField: FC<UploadFieldProps> = ({ item, input, children, ...props }) => {
  const [{ onChange: baseOnChange, ...field }, meta, helper] = useField<File | null>(props);
  const [previewUrl, setPreviewUrl] = useState<null | string>(null);
  const fileList = useMemo(() => (field.value ? [field.value as unknown as UploadFile] : []), [field.value]);

  const onChange = useCallback(
    ({ file }: UploadChangeParam) => {
      if (file.status === 'removed') {
        baseOnChange(field.name)({ target: { type: 'text', value: null } } as any);
      } else {
        baseOnChange(field.name)({ target: { type: 'text', value: file } } as any);
      }
    },
    [baseOnChange, field.name],
  );

  useEffect(() => {
    (async () => {
      if (field.value) {
        const base64Response = await getBase64(field.value);
        if (base64Response) {
          setPreviewUrl(base64Response);
        } else {
          helper.setValue(null);
        }
      } else {
        setPreviewUrl(null);
      }
    })();
  }, [field.value, helper]);

  const newField = useMemo(() => ({ onChange, fileList }), [onChange, fileList]);

  return <MemoizedUploadField field={newField} meta={meta} input={input} item={item} previewUrl={previewUrl} children={children} />;
};
